// /host/:code#h=<token> — the teacher's deep link from POST /sessions.
//
// On mount:
//   1. Read hostToken from location.hash (#h=<token>).
//   2. history.replaceState to strip the hash before anything else logs it.
//   3. Persist the token to sessionStorage (tab-scoped — dies on close).
//   4. Fetch the session to confirm it exists.
//   5. Forward to /host/lobby with minimal state. The lobby gets the token
//      in route state; HostQuestion re-attaches after a dropped socket with
//      the copy saved in sessionStorage (useHostAttach).
//
// If the URL is missing a token or the session is unknown the teacher is
// bounced back to /host to re-create.

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DEFAULT_TIME_LIMIT_MS, isValidSessionCode } from "@binogi-livequiz/shared";
import Wordmark from "../components/Wordmark";
import { api, ApiError } from "../lib/api";
import { saveHostToken } from "../lib/session-storage";

type Phase = "loading" | "error_token" | "error_lookup";

function readAndStripHostToken(): string | null {
  const hash = window.location.hash;
  if (!hash || !hash.startsWith("#")) return null;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get("h");
  try {
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );
  } catch {
    // best-effort
  }
  return token;
}

export default function HostEntry() {
  const params = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const code = (params.code ?? "").toUpperCase();
  const [phase, setPhase] = useState<Phase>("loading");

  // Lazy ref so the hash is read exactly once on first render — strictly
  // before the hash-strip side effect runs from any other code path.
  const tokenRef = useRef<string | null>(null);
  const tokenReadRef = useRef(false);
  if (!tokenReadRef.current) {
    tokenReadRef.current = true;
    tokenRef.current = readAndStripHostToken();
  }

  useEffect(() => {
    if (!isValidSessionCode(code)) {
      setPhase("error_token");
      return;
    }
    const token = tokenRef.current;
    if (!token) {
      setPhase("error_token");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const info = await api.getSession(code);
        if (cancelled) return;
        saveHostToken(code, token);
        navigate("/host/lobby", {
          replace: true,
          state: {
            sessionCode: code,
            joinUrl: `${window.location.origin}/join/${code}`,
            hostToken: token,
            // We don't have questions / settings here — the deep-link
            // path is mostly for the teacher's second device. HostQuestion
            // takes the question count and time limit from each
            // QUESTION_START, so an empty array only means round 1 shows
            // once the server's QUESTION_START arrives.
            questions: [],
            timeLimitMs: DEFAULT_TIME_LIMIT_MS,
            nameMode: info.nameMode,
            expectedStudents: info.expectedStudents,
          },
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setPhase("error_lookup");
        } else {
          setPhase("error_lookup");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, navigate]);

  if (phase === "loading") {
    return (
      <main className="min-h-full flex items-center justify-center px-6 text-center">
        <div className="inline-flex items-center gap-3 text-white/70">
          <span className="w-3 h-3 rounded-full bg-brand-green animate-pulseSoft" />
          <span>{t("host.entry.opening")}</span>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-full flex flex-col">
      <header className="px-6 py-5 flex justify-center">
        <Wordmark size="md" />
      </header>
      <section className="flex-1 flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-extrabold mb-3">
            {phase === "error_token"
              ? t("host.entry.token_title")
              : t("host.entry.not_found_title")}
          </h1>
          <p className="text-white/60 mb-6">
            {phase === "error_token"
              ? t("host.entry.token_body")
              : t("host.entry.not_found_body")}
          </p>
          <button
            type="button"
            onClick={() => navigate("/host", { replace: true })}
            className="px-6 py-3 bg-brand-green hover:bg-brand-greenHi rounded-xl font-bold"
          >
            {t("host.entry.back")}
          </button>
        </div>
      </section>
    </main>
  );
}
